import React, { useState, useEffect } from 'react';
import './Results.css';

function Results() {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedResult, setSelectedResult] = useState(null);

  useEffect(() => {
    fetchResults();
  }, []);

  const fetchResults = async () => {
    try {
      const response = await fetch('https://careerquest.scaleo.hr/api/v1/results');
      const data = await response.json();
      setResults(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching results:', error);
      setLoading(false);
    }
  };

  const deleteResult = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce résultat ?')) {
      try {
        await fetch(`https://careerquest.scaleo.hr/api/v1/results/${id}`, {
          method: 'DELETE',
        });
        fetchResults();
        setSelectedResult(null);
      } catch (error) {
        console.error('Error deleting result:', error);
      }
    }
  };

  const downloadPDF = async (id, e) => {
    e.stopPropagation();
    try {
      const response = await fetch(`https://careerquest.scaleo.hr/api/v1/results/${id}/pdf`);
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `career_quest_${id}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        alert('Erreur lors de la génération du PDF');
      }
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Erreur lors du téléchargement du PDF');
    }
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  if (loading) {
    return (
      <div className="results-container">
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '400px',
          flexDirection: 'column',
          gap: '16px'
        }}>
          <div style={{ 
            width: '48px', 
            height: '48px', 
            border: '4px solid #E5E7EB', 
            borderTop: '4px solid #4EAEF5',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <p style={{ color: '#6B7280', fontWeight: '600' }}>Chargement des résultats...</p>
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      </div>
    );
  }

  const totalAnswers = results.reduce((sum, r) => sum + Object.keys(r.answers).length, 0);
  const averageScore = results.length > 0 
    ? Math.round(results.reduce((sum, r) => sum + (r.results.families[0]?.percentage || 0), 0) / results.length)
    : 0;

  return (
    <div className="results-container">
      <div className="results-header">
        <div className="header-content">
          <h1>Tableau de bord des résultats</h1>
          <p className="header-subtitle">Analyse complète des évaluations de carrière</p>
        </div>
        <div className="stats-overview">
          <div className="stat-card">
            <div className="stat-value">{results.length}</div>
            <div className="stat-label">Évaluations totales</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{totalAnswers}</div>
            <div className="stat-label">Réponses collectées</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{averageScore}%</div>
            <div className="stat-label">Score moyen maximum</div>
          </div>
        </div>
      </div>
      
      <div className="results-layout">
        <div className="results-list">
          <div className="list-header">
            <h2>Historique des évaluations</h2>
            <span className="result-count">{results.length} {results.length === 1 ? 'Résultat' : 'Résultats'}</span>
          </div>
          {results.length === 0 ? (
            <div className="empty-state">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="empty-state-title">Aucune évaluation disponible</p>
              <p className="empty-state-text">Complétez une évaluation pour voir les résultats détaillés et les informations ici.</p>
            </div>
          ) : (
            <div className="results-grid">
              {results.map((result) => (
                <div
                  key={result._id}
                  className={`result-card ${selectedResult?._id === result._id ? 'selected' : ''}`}
                  onClick={() => setSelectedResult(result)}
                >
                  <div className="result-header">
                    <div className="user-info">
                      <div className="user-badge">{result.userId.charAt(0).toUpperCase()}</div>
                      <strong>{result.userId}</strong>
                    </div>
                    <div className="result-actions">
                      <button
                        className="action-btn download-btn"
                        onClick={(e) => downloadPDF(result._id, e)}
                        title="Télécharger le PDF"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </button>
                      <button
                        className="action-btn delete-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteResult(result._id);
                        }}
                        title="Supprimer l'évaluation"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  <div className="result-date">{formatDate(result.timestamp)}</div>
                  <div className="result-preview">
                    {result.results.families.length > 0 && (
                      <div className="top-result">
                        <span className="top-result-label">Meilleure correspondance</span>
                        <span className="top-result-value">{result.results.families[0].name}</span>
                        <span className="top-result-percentage">{result.results.families[0].percentage}%</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {selectedResult && (
          <div className="result-details">
            <div className="details-header">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h2>Analyse de l'évaluation</h2>
                <button
                  onClick={(e) => downloadPDF(selectedResult._id, e)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    background: '#42a5f5',
                    color: 'white',
                    border: 'none',
                    padding: '10px 20px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '14px',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 2px 4px rgba(66, 165, 245, 0.2)'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = '#1e88e5';
                    e.target.style.transform = 'translateY(-1px)';
                    e.target.style.boxShadow = '0 4px 8px rgba(66, 165, 245, 0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = '#42a5f5';
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 2px 4px rgba(66, 165, 245, 0.2)';
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Télécharger le PDF
                </button>
              </div>
              <div className="assessment-meta">
                <div className="meta-item">
                  <span className="meta-label">Participant</span>
                  <span className="meta-value">{selectedResult.userId}</span>
                </div>
                <div className="meta-item">
                  <span className="meta-label">Terminé le</span>
                  <span className="meta-value">{formatDate(selectedResult.timestamp)}</span>
                </div>
                <div className="meta-item">
                  <span className="meta-label">Réponses</span>
                  <span className="meta-value">{Object.keys(selectedResult.answers).length} Questions</span>
                </div>
              </div>
            </div>

            {/* Summary Box */}
            <div className="results-summary-box">
              <p style={{ fontSize: '16px', lineHeight: '1.8', marginBottom: '16px' }}>
                Vos résultats indiquent que votre profil est plus orienté vers des métiers de <strong style={{ color: '#3B82F6' }}>{selectedResult.results.families[0]?.name}</strong>
                {selectedResult.results.families[1] && <span> et <strong style={{ color: '#3B82F6' }}>{selectedResult.results.families[1].name}</strong></span>}.
              </p>
              <p style={{ fontSize: '16px', lineHeight: '1.8', marginBottom: '8px' }}>
                Votre profil personnel relève vos points forts :
              </p>
              <ul style={{ fontSize: '16px', lineHeight: '1.8', margin: 0, paddingLeft: '24px' }}>
                <li><strong style={{ color: '#3B82F6' }}>{selectedResult.results.subjobs[0]?.name}</strong></li>
                {selectedResult.results.subjobs[1] && <li><strong style={{ color: '#3B82F6' }}>{selectedResult.results.subjobs[1]?.name}</strong></li>}
                {selectedResult.results.subjobs[2] && <li><strong style={{ color: '#3B82F6' }}>{selectedResult.results.subjobs[2]?.name}</strong></li>}
              </ul>
            </div>

            <div className="detail-section">
              <div className="section-header">
                <h3>Familles de métiers recommandées pour vous</h3>
                <p className="section-description">Vos affinités par famille de métiers</p>
              </div>
              {selectedResult.results.families.map((family, idx) => (
                <div key={idx} className="score-item">
                  <div className="score-label">
                    {family.name}
                  </div>
                  <div className="score-bar-container">
                    <div className="score-bar-center-line"></div>
                    <div
                      className="score-bar"
                      style={{
                        left: family.percentage >= 0 ? '50%' : `${50 + (family.percentage / 2)}%`,
                        width: `${Math.abs(family.percentage) / 2}%`
                      }}
                    />
                    <span className="score-value">{family.percentage}%</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="detail-section">
              <div className="section-header">
                <h3>Vos compétences fortes</h3>
                <p className="section-description">Détail de vos points forts par domaine de compétences</p>
              </div>
              {selectedResult.results.subjobs.map((subjob, idx) => (
                <div key={idx} className="score-item">
                  <div className="score-label">
                    {subjob.name}
                  </div>
                  <div className="score-bar-container">
                    <div className="score-bar-center-line"></div>
                    <div
                      className="score-bar"
                      style={{
                        left: subjob.percentage >= 0 ? '50%' : `${50 + (subjob.percentage / 2)}%`,
                        width: `${Math.abs(subjob.percentage) / 2}%`
                      }}
                    />
                    <span className="score-value">{subjob.percentage}%</span>
                  </div>
                </div>
              ))}
            </div>

            {selectedResult.results.recommended_sous_metiers && selectedResult.results.recommended_sous_metiers.length > 0 && (
              <div className="detail-section">
                <div className="section-header">
                  <h3>Parcours professionnels recommandés</h3>
                  <p className="section-description">Recommandations personnalisées basées sur votre profil</p>
                </div>
                <div className="recommended-careers">
                  {selectedResult.results.recommended_sous_metiers.map((metier, idx) => (
                    <div key={idx} className="career-recommendation">
                      <svg className="career-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>{metier}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            
          </div>
        )}
      </div>
    </div>
  );
}

export default Results;
